import { Component, Input, OnInit } from '@angular/core';

import { EMPTY, Observable, catchError, combineLatest, filter, map } from 'rxjs';

import { hasUnitPrices } from '@fcom/common/utils';
import { SentryLogger } from '@fcom/core/services';
import {
  FinnairAmount,
  FinnairCharge,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPrice,
  FinnairServiceCategoryPriceItem,
  FinnairTotalPricesDetails,
} from '@fcom/dapi/api/models';
import { Amount, PriceService } from '@fcom/dapi/interfaces';
import { asPaxTypeKey } from '@fcom/dapi/utils';
import { ModalButtons } from '@fcom/ui-components';
import { isPresent } from '@fcom/core/utils';
import { ElementActions, ElementTypes } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';

interface BreakdownModel extends FinnairPassengerItem {
  prices: FinnairPrice;
  services: PriceService[];
  total: FinnairAmount;
}

const hasPassengers = (passengers: FinnairPassengerItem[]): boolean => isPresent(passengers) && passengers.length > 0;

@Component({
  selector: 'fin-price-breakdown-renewed',
  templateUrl: './price-breakdown-renewed.component.html',
  styleUrls: ['./price-breakdown-renewed.component.scss'],
})
export class PriceBreakdownRenewedComponent implements OnInit {
  public readonly ModalButtons = ModalButtons;
  public readonly asPaxTypeKey = asPaxTypeKey;
  public readonly FinnairPassengerCode = FinnairPassengerCode;

  @Input()
  prices$: Observable<FinnairTotalPricesDetails>;
  @Input()
  passengers$: Observable<FinnairPassengerItem[]>;
  @Input()
  showBaseFare = true;
  @Input()
  showTotalTax = true;
  @Input()
  showPoints = false;
  @Input()
  expandOpen = true;

  breakdowns$: Observable<BreakdownModel[]>;
  totalPrice$: Observable<Amount>;
  taxModalOpen = false;
  taxModalData: { totalTaxes: FinnairAmount };
  summary$: Observable<PriceService[]>;
  summaryModalOpen: boolean;
  summaryModalData: { prices: FinnairTotalPricesDetails };
  totalTaxes$: Observable<FinnairCharge[]>;

  constructor(
    private sentryLogger: SentryLogger,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.breakdowns$ = combineLatest([this.passengers$, this.prices$]).pipe(
      filter(([passengers, prices]) => hasPassengers(passengers) && hasUnitPrices(prices)),
      map(([passengers, prices]) =>
        passengers
          .filter((passenger) => parseFloat(prices.total.totalPerPax?.[passenger.id]?.totalAmount.amount) > 0)
          .map((passenger: FinnairPassengerItem) => ({
            ...passenger,
            prices: prices.flight?.totalPerPax?.[passenger.id] || prices.services?.totalPerPax?.[passenger.id],
            services: this.getServicesForPax(prices.services?.totalPerCategory, passenger),
            total: prices.total.totalPerPax?.[passenger.id]?.totalAmount,
          }))
      ),
      catchError((e: unknown) => {
        this.sentryLogger.error('Exception in handling prices of passengers', e);
        return EMPTY;
      })
    );

    this.summary$ = combineLatest([this.passengers$, this.prices$]).pipe(
      filter(([passengers, prices]) => hasPassengers(passengers) && hasUnitPrices(prices)),
      map(([passengers, prices]) => {
        const summary = passengers.reduce<{ [key: string]: PriceService }>(
          (summaryAcc, passenger: FinnairPassengerItem) => {
            const passengerSummary = this.getServicesForPax(prices.services?.totalPerCategory, passenger);

            passengerSummary.forEach((service) => {
              if (summaryAcc[service.id]) {
                const totalAmount = parseFloat(summaryAcc[service.id].total.amount) + parseFloat(service.total.amount);
                summaryAcc[service.id] = {
                  ...summaryAcc[service.id],
                  total: {
                    ...summaryAcc[service.id].total,
                    amount: totalAmount.toString(),
                  },
                  count: service.count + summaryAcc[service.id].count,
                };
              } else {
                summaryAcc[service.id] = { ...service };
              }
            });
            return summaryAcc;
          },
          {}
        );

        return Object.values(summary);
      })
    );

    this.totalTaxes$ = combineLatest([this.passengers$, this.prices$]).pipe(
      filter(([passengers, prices]) => hasPassengers(passengers) && hasUnitPrices(prices)),
      map(([passengers, prices]) => {
        const totalTaxes = passengers.reduce<{ [key: string]: FinnairCharge }>(
          (taxAcc, passenger: FinnairPassengerItem) => {
            const passengerTaxes = prices.total.totalPerPax[passenger.id]?.taxes;

            passengerTaxes?.forEach((tax) => {
              if (taxAcc[tax.code]) {
                taxAcc[tax.code].amount = (parseFloat(taxAcc[tax.code].amount) + parseFloat(tax.amount)).toString();
              } else {
                taxAcc[tax.code] = { ...tax };
              }
            });
            return taxAcc;
          },
          {}
        );

        return Object.values(totalTaxes);
      })
    );
  }

  openTaxModal(event, totalTaxes: FinnairAmount) {
    event.stopPropagation();

    this.taxModalOpen = true;
    this.taxModalData = { totalTaxes };

    this.gtmService.trackElement(
      'taxes-fees-charges-modal',
      'booking-flow_purchase-review',
      ElementTypes.BUTTON,
      '',
      ElementActions.CLICK
    );
  }

  openSummaryModal(event, prices: FinnairTotalPricesDetails) {
    event.stopPropagation();

    this.summaryModalOpen = true;
    this.summaryModalData = {
      prices,
    };

    this.gtmService.trackElement(
      'detailed-breakdown-modal',
      'booking-flow_purchase-review',
      ElementTypes.BUTTON,
      '',
      ElementActions.CLICK
    );
  }

  closeSummaryModal() {
    this.summaryModalData = null;
  }

  closeTaxModal() {
    this.taxModalData = null;
  }

  getServicesForPax(
    pricesForCategory: FinnairServiceCategoryPriceItem[] = [],
    passenger: FinnairPassengerItem
  ): PriceService[] {
    const sortOrder: string[] = ['seat', 'baggage', 'meal', 'wifi', 'travelComfort', 'lounge', 'cover'].reverse();
    return pricesForCategory
      .map((c) => {
        const name = `${c.category}Count`;
        const count = c.totalPerPax[passenger.id]?.quantity ?? 0;
        const totalAmount = c.totalPerPax[passenger.id]?.price.totalAmount;
        const originalTotalAmount = c.totalPerPax[passenger.id]?.price.originalTotalAmount;
        return {
          id: c.category,
          total: totalAmount ?? null,
          originalTotal: originalTotalAmount ?? null,
          count,
          key: `purchase.payment.${name}`,
          [name]: count,
        };
      })
      .filter((service) => service.count > 0)
      .sort((a, b) => sortOrder.indexOf(b.id) - sortOrder.indexOf(a.id));
  }
}
